import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from '../../utility/fetchWeb';
import axios from 'axios';
import { ArrowLeft, Star, MapPin, Phone, Mail, Clock, Calendar, MessageSquare, Video, ChevronLeft, ChevronRight, Award, Users, Building2, Stethoscope, Activity, Shield } from 'lucide-react';
import Map from '../Layout/Map';
import Pagination from '../Layout/Pagination';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const Profile = () => {
    const location = useLocation();
    const [data, setData] = useState(location.state?.providerData || null);
    const [loading, setLoading] = useState(!location.state?.providerData);
    const [services, setServices] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [equipment, setEquipment] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [currentEquipmentIndex, setCurrentEquipmentIndex] = useState(0);
    const [aboutUs, setAboutUs] = useState(null);
    const query = useQuery();
    const navigate = useNavigate();

    const getData = async () => {
        try {
            setLoading(true);
            let response;
            const category = query?.get("cat");
            const id = query?.get("id");

            if (location.state?.providerData) {
                response = location.state.providerData;
                setData(response);
            } else {
                if (category === 'doctor' || category === 'practitioner') {
                    response = await get(`/doctors/doctors/${id}`);
                    if (response?.id) {
                        await fetchDoctorServices(response.id, 1);
                    }
                } else if (category === 'hospital') {
                    response = await get(`/hospitals/list`);
                } else if (category === 'lab') {
                    response = await get(`/lab/list`);
                } else if (category === 'pharmacy') {
                    response = await get(`/pharmacy/list`);
                } else if (category === 'caregiver') {
                    response = await get(`/doctors/doctors/${id}`);
                } else {
                    response = await get(`/healthCareProvider/healthCareProvider/id/${id}`);
                    if (response?.healthCareProviderCategory?.id) {
                        await fetchServices(response.id, response.healthCareProviderCategory.id, 1);
                    }
                }
                setData(response);
            }

            const clinicId = response?.clinicId;
            if (clinicId && category !== 'doctor' && category !== 'practitioner') {
                try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const [equipmentRes, doctorsRes, aboutLabRes] = await Promise.all([
                        fetch(`${backendUrl}/equipment/${clinicId}`),
                        fetch(`${backendUrl}/doctors/${clinicId}`),
                        fetch(`${backendUrl}/aboutLab/healthcareprovider/id/${location.state?.providerData?.id}`),
                    ]);

                    const [equipmentData, doctorsData, aboutLabData] = await Promise.all([
                        equipmentRes.json(),
                        doctorsRes.json(),
                        aboutLabRes.json(),
                    ]);

                    if (Array.isArray(equipmentData)) {
                        setEquipment(equipmentData);
                    }
                    if (Array.isArray(doctorsData)) {
                        setDoctors(doctorsData);
                    }
                    if (aboutLabData && !Array.isArray(aboutLabData)) {
                        setAboutUs(aboutLabData);
                    }

                    if (category === 'hospital' || response?.healthCareProviderCategory?.id === 5) {
                        try {
                            const servicesRes = await fetch(`${backendUrl}/services/${clinicId}`);
                            const servicesData = await servicesRes.json();
                            if (Array.isArray(servicesData)) {
                                setServices(servicesData);
                            }
                        } catch (err) {
                            console.error('Failed to load services:', err);
                        }
                    }
                } catch (err) {
                    console.error('Failed to load equipment or doctors:', err);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDoctorServices = async (userId, page = 1) => {
        try {
            setServicesLoading(true);
            const token = sessionStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/costsetup/user/${userId}?pageNumber=${page}&pageSize=10`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const serviceData = response?.data?.items || [];
            setServices(serviceData);
            setTotalPages(Math.ceil((response?.data?.totalCount || 0) / 10));
            setCurrentPage(response?.data?.pageNumber || page);
        } catch (error) {
            console.error('Error fetching doctor services:', error);
            setServices([]);
        } finally {
            setServicesLoading(false);
        }
    };

    const fetchServices = async (providerId, categoryId, page = 1) => {
        try {
            setServicesLoading(true);
            let response;

            if (categoryId === 4) {
                response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/internallabservice/list/${providerId}/${page}/10/by-health-care-provider`
                );
                const serviceData = response?.data?.data?.recordList || response?.data?.recordList || [];
                setServices(serviceData);
                setTotalPages(response?.data?.data?.metadata?.totalPages || response?.data?.metadata?.totalPages || 1);
            } else {
                setServices([]);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            setServices([]);
        } finally {
            setServicesLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        const category = query?.get("cat");
        if (category === 'doctor' || category === 'practitioner') {
            if (data?.id) {
                fetchDoctorServices(data.id, page);
            }
        } else if (data?.id && data?.healthCareProviderCategory?.id) {
            fetchServices(data.id, data.healthCareProviderCategory.id, page);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    const getServiceName = (service) => {
        return service.service || service.testName || service.productName || service.name || service.serviceName || 'Service';
    };

    const getServiceDescription = (service) => {
        return service.additionalInfo || service.description || service.howToUseIt || 'No description available';
    };

    const getServicePrice = (service) => {
        const price = service.cost || service.price || service.sellingPrice || service.amount;
        return price ? `₦${price.toLocaleString()}` : 'Price on request';
    };

    if (loading) {
        return (
            <div className="w-100 min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="space-y-8">
                        <div className="h-12 bg-slate-200 rounded-lg w-48"></div>
                        <div className="h-96 bg-slate-200 rounded-3xl"></div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2 space-y-6">
                                <div className="h-64 bg-slate-200 rounded-2xl"></div>
                                <div className="h-64 bg-slate-200 rounded-2xl"></div>
                            </div>
                            <div className="space-y-6">
                                <div className="h-48 bg-slate-200 rounded-2xl"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-100 min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30">
            {/* Decorative background elements */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-green-100/30 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100/20 rounded-full blur-3xl"></div>
            </div>

            {/* Navigation */}
            <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-3 text-slate-600 hover:text-green-600 transition-all duration-300 group"
                        >
                            <div className="p-2 rounded-xl bg-slate-100 group-hover:bg-green-50 transition-colors">
                                <ArrowLeft className="w-5 h-5" />
                            </div>
                            <span className="font-medium text-sm tracking-wide">Back to Results</span>
                        </button>

                        <button
                            onClick={() => navigate(`/book-appointment?cat=${query?.get("cat")}&id=${query?.get("id")}`)}
                            className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300 hover:-translate-y-0.5"
                        >
                            Book Appointment
                        </button>
                    </div>
                </div>
            </nav>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Hero Section */}
                <div className="relative mb-16">
                    <div className="bg-gradient-to-br from-white to-green-50/50 rounded-3xl shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200/60">
                        {/* Header Image/Banner */}
                        <div className="relative h-64 bg-gradient-to-br from-green-600 via-green-500 to-emerald-600 overflow-hidden">
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute inset-0" style={{
                                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)',
                                }}></div>
                            </div>
                            {(data?.logoPath || data?.profilePicture || data?.healthCareProvider?.logoPath) && (
                                <img
                                    src={data?.logoPath || data?.profilePicture || data?.healthCareProvider?.logoPath}
                                    alt="Banner"
                                    className="w-full h-full object-cover opacity-20"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}

                            {/* Floating stats */}
                            <div className="absolute top-6 right-6 flex gap-3">
                                {(data?.status === 1 || data?.accountStatus === 'Activate') && (
                                    <div className="px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full flex items-center gap-2 shadow-lg">
                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                        <span className="text-sm font-semibold text-slate-700">Active Now</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Profile Content */}
                        <div className="relative px-8 pb-8">
                            {/* Profile Picture */}
                            <div className="relative -mt-16 mb-6">
                                <div className="inline-block">
                                    <div className="relative">
                                        {(data?.logoPath || data?.profilePicture || data?.healthCareProvider?.logoPath) ? (
                                            <img
                                                src={data?.logoPath || data?.profilePicture || data?.healthCareProvider?.logoPath}
                                                alt="Profile"
                                                className="w-32 h-32 rounded-2xl object-cover border-4 border-white shadow-xl"
                                                onError={(e) => {
                                                    e.target.src = "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop";
                                                }}
                                            />
                                        ) : (
                                            <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100 border-4 border-white shadow-xl flex items-center justify-center">
                                                <Stethoscope className="w-12 h-12 text-green-600" />
                                            </div>
                                        )}
                                        <div className="absolute -bottom-2 -right-2 p-2 bg-green-500 rounded-xl shadow-lg">
                                            <Shield className="w-5 h-5 text-white" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Title and Info */}
                            <div className="space-y-4">
                                <div>
                                    <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">
                                        {(() => {
                                            const individualName = `${data?.title || ''} ${data?.firstName || ''} ${data?.lastName || ''}`.trim();
                                            if (individualName) return individualName;
                                            return data?.name || data?.brandName || data?.healthCareProvider?.name || 'Healthcare Provider';
                                        })()}
                                    </h1>

                                    <div className="flex flex-wrap items-center gap-3">
                                        {(data?.healthCareProviderCategory?.name || data?.healthCareProvider?.category) && (
                                            <span className="px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                                {data?.healthCareProviderCategory?.name || data?.healthCareProvider?.category}
                                            </span>
                                        )}
                                        {data?.userRoles && data.userRoles.length > 0 && (
                                            <span className="px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                                {data.userRoles[0]?.role?.name}
                                            </span>
                                        )}
                                        {data?.userRoles && data.userRoles[0]?.roleSpecialist?.specialistName && (
                                            <span className="px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                                {data.userRoles[0].roleSpecialist.specialistName}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Rating and Quick Info */}
                                <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-slate-200">
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className={`w-5 h-5 ${i < Math.floor(data?.rating || 0)
                                                            ? 'fill-yellow-400 text-yellow-400'
                                                            : 'text-slate-300'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                        <span className="text-sm font-semibold text-slate-700">
                                            {(data?.rating || 0).toFixed(1)}
                                        </span>
                                        <span className="text-sm text-slate-500">
                                            ({data?.reviews || 0} reviews)
                                        </span>
                                    </div>

                                    {data?.yearsOfExperience && (
                                        <div className="flex items-center gap-2 text-slate-600">
                                            <Award className="w-5 h-5 text-green-600" />
                                            <span className="text-sm font-medium">{data.yearsOfExperience} years experience</span>
                                        </div>
                                    )}

                                    {data?.department && (
                                        <div className="flex items-center gap-2 text-slate-600">
                                            <Building2 className="w-5 h-5 text-blue-600" />
                                            <span className="text-sm font-medium">{data.department}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="w-100 grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column - Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* About Section */}
                        {(aboutUs?.labDetails || aboutUs?.thingToKnow || data?.mandate || data?.description || data?.aboutCareGiver) && (
                            <section className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-8 border border-slate-200/60">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-green-100 rounded-xl">
                                        <Activity className="w-6 h-6 text-green-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-slate-900">
                                        About {data?.healthCareProviderCategory?.name || (data?.userRoles && data.userRoles[0]?.roleSpecialist?.specialistName) || 'Us'}
                                    </h2>
                                </div>

                                <div className="prose prose-slate max-w-none">
                                    <p className="text-slate-600 leading-relaxed text-base">
                                        {aboutUs?.labDetails || data?.aboutCareGiver || data?.mandate || data?.description ||
                                            "Welcome to our healthcare facility. We are committed to providing quality healthcare services to our community with compassion, expertise, and excellence."}
                                    </p>
                                </div>

                                {aboutUs?.workDays && (
                                    <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-green-600" />
                                            Working Hours
                                        </h3>
                                        <p className="text-slate-600 text-sm">{aboutUs.workDays}</p>
                                    </div>
                                )}

                                {aboutUs?.thingToKnow && (
                                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                                        <h3 className="text-sm font-semibold text-blue-900 mb-2">Important Information</h3>
                                        <p className="text-blue-700 text-sm">{aboutUs.thingToKnow}</p>
                                    </div>
                                )}
                            </section>
                        )}

                        {/* Services Section */}
                        {(services.length > 0 || servicesLoading) && (
                            <section className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-8 border border-slate-200/60">
                                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                                    {data?.healthCareProviderCategory?.id === 4 ? 'Available Laboratory Tests' :
                                        data?.healthCareProviderCategory?.id === 3 ? 'Available Products' :
                                            data?.userRoles ? 'Services We Offer' : 'Our Services'}
                                </h2>

                                {servicesLoading ? (
                                    <div className="grid grid-cols-2 gap-5">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="animate-pulse">
                                                <div className="h-40 bg-slate-100 rounded-xl"></div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex space-between flex-wrap gap-5">
                                            {services.map((service, index) => (
                                                <div
                                                    key={index}
                                                    className="group p-6 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 hover:border-green-300 hover:shadow-lg hover:shadow-green-100/50 transition-all duration-300 min-h-[160px] w-[48%] flex flex-col"
                                                >
                                                    <h3 className="text-base font-semibold text-slate-900 group-hover:text-green-600 transition-colors mb-3 line-clamp-2">
                                                        {getServiceName(service)}
                                                    </h3>
                                                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">
                                                        {getServiceDescription(service)}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>

                                        {totalPages > 1 && (
                                            <div className="mt-8">
                                                <Pagination
                                                    currentPage={currentPage}
                                                    totalPages={totalPages}
                                                    onPageChange={handlePageChange}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>
                        )}

                        {/* Equipment Gallery */}
                        {equipment.length > 0 && (
                            <section className="w-100 bg-white rounded-2xl shadow-lg shadow-slate-200/50 overflow-hidden border border-slate-200/60">
                                <div className="p-8 pb-0">
                                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Our Equipment</h2>
                                </div>

                                <div className="relative">
                                    <img
                                        src={equipment[currentEquipmentIndex]?.imageUrl || "https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1200&h=600&fit=crop"}
                                        alt={equipment[currentEquipmentIndex]?.name}
                                        className="w-full h-[760px] object-cover"
                                        onError={(e) => {
                                            e.target.src = "https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1200&h=600&fit=crop";
                                        }}
                                    />

                                    {/* Navigation Buttons */}
                                    {equipment.length > 1 && (
                                        <>
                                            <button
                                                onClick={() => setCurrentEquipmentIndex(prev => prev > 0 ? prev - 1 : equipment.length - 1)}
                                                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg hover:bg-white transition-all duration-300"
                                            >
                                                <ChevronLeft className="w-6 h-6 text-slate-700" />
                                            </button>
                                            <button
                                                onClick={() => setCurrentEquipmentIndex(prev => prev < equipment.length - 1 ? prev + 1 : 0)}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg hover:bg-white transition-all duration-300"
                                            >
                                                <ChevronRight className="w-6 h-6 text-slate-700" />
                                            </button>
                                        </>
                                    )}

                                    {/* Counter */}
                                    <div className="absolute bottom-4 right-4 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm font-medium">
                                        {currentEquipmentIndex + 1} / {equipment.length}
                                    </div>
                                </div>

                                <div className="p-8">
                                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                                        {equipment[currentEquipmentIndex]?.name}
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed">
                                        {equipment[currentEquipmentIndex]?.description}
                                    </p>
                                </div>
                            </section>
                        )}

                        {/* Care Team */}
                        {doctors.length > 0 && (
                            <section className="w-100 bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-8 border border-slate-200/60">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100 rounded-xl">
                                        <Users className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-slate-900">Our Care Team</h2>
                                </div>

                                <div className="flex flex-wrap gap-5 justify-between">
                                    {doctors.map((member, index) => (
                                        <div
                                            key={index}
                                            className="w-[30%] text-center"
                                        >
                                            <img
                                                src={
                                                    member.profileImage ||
                                                    "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop"
                                                }
                                                alt={member.fullName}
                                                className="w-full aspect-square rounded-2xl object-cover mx-auto mb-4 transition-shadow duration-300"
                                                onError={(e) => {
                                                    e.target.src =
                                                        "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop";
                                                }}
                                            />

                                            <h3 className="font-semibold text-slate-900 mb-1 break-words">
                                                {member.fullName}
                                            </h3>
                                            <p className="text-sm text-slate-600 break-words">
                                                {member.specialty}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                            </section>
                        )}

                        {/* Map Section */}
                        {(data?.location || data?.healthCareProvider?.location) && (
                            <section className="w-100 bg-white rounded-2xl shadow-lg shadow-slate-200/50 overflow-hidden border border-slate-200/60">
                                <div className="p-8 pb-0">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2 bg-green-100 rounded-xl">
                                            <MapPin className="w-6 h-6 text-green-600" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-slate-900">Location</h2>
                                    </div>
                                </div>
                                <div className="h-[500px]">
                                    <Map />
                                </div>
                            </section>
                        )}
                    </div>

                </div>

                {/* Contact Information Section - Full Width */}
                <div className="mt-12">
                    <section className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200/60 overflow-hidden">
                        <div className="bg-gradient-to-r from-slate-50 to-green-50/30 px-8 py-6 border-b border-slate-200">
                            <h2 className="text-2xl font-bold text-slate-900">Contact Information</h2>
                            <p className="text-slate-600 text-sm mt-1">Get in touch with us</p>
                        </div>

                        <div className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {(data?.email || data?.username) && (
                                    <a
                                        href={`mailto:${data.email || data.username}`}
                                        className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl hover:bg-green-50 hover:shadow-md transition-all duration-300 group border border-slate-200 hover:border-green-300"
                                    >
                                        <div className="p-3 bg-white rounded-xl group-hover:bg-green-100 transition-colors shadow-sm">
                                            <Mail className="w-6 h-6 text-slate-600 group-hover:text-green-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Email</p>
                                            <p className="text-sm font-medium text-slate-900 break-words">{data.email || data.username}</p>
                                        </div>
                                    </a>
                                )}

                                {(data?.phone || data?.phoneNumber) && (
                                    <a
                                        href={`tel:${data.phone || data.phoneNumber}`}
                                        className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl hover:bg-green-50 hover:shadow-md transition-all duration-300 group border border-slate-200 hover:border-green-300"
                                    >
                                        <div className="p-3 bg-white rounded-xl group-hover:bg-green-100 transition-colors shadow-sm">
                                            <Phone className="w-6 h-6 text-slate-600 group-hover:text-green-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Phone</p>
                                            <p className="text-sm font-medium text-slate-900">{data.phone || data.phoneNumber}</p>
                                        </div>
                                    </a>
                                )}

                                {(data?.location || data?.healthCareProvider?.location) && (
                                    <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                                        <div className="p-3 bg-white rounded-xl shadow-sm">
                                            <MapPin className="w-6 h-6 text-slate-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Address</p>
                                            <p className="text-sm font-medium text-slate-900 leading-relaxed break-words">
                                                {data.location || data.healthCareProvider?.location}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {data?.workingHours && (
                                    <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                                        <div className="p-3 bg-white rounded-xl shadow-sm">
                                            <Clock className="w-6 h-6 text-slate-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Hours</p>
                                            <p className="text-sm font-medium text-slate-900">{data.workingHours}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-8 flex justify-center">
                                <button
                                    onClick={() => navigate(`/book-appointment?cat=${query?.get("cat")}&id=${query?.get("id")}`)}
                                    className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300 hover:-translate-y-0.5"
                                >
                                    Schedule an Appointment
                                </button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Profile;